import React, { useRef, useEffect, useMemo } from "react";
import { Canvas } from "@react-three/fiber";
import { OrbitControls, Environment } from "@react-three/drei";
import * as THREE from "three";
import "../styles/playgame.css";

const CinematicSpotlight = ({ position, targetX }) => {
  const spotLightRef = useRef();
  const targetRef = useRef();

  useEffect(() => {
    if (spotLightRef.current && targetRef.current) {
      spotLightRef.current.target = targetRef.current;
    }
  }, []);

  return (
    <group position={position}>
      <object3D
        ref={targetRef}
        position={[targetX - position[0], -position[1] + 0.1, 0.5 - position[2]]}
      />

      <mesh position={[0, -0.15, 0]} castShadow>
        <cylinderGeometry args={[0.15, 0.15, 0.3, 24]} />
        <meshStandardMaterial color="#0b0b0b" metalness={0.9} roughness={0.2} />
      </mesh>

      <mesh position={[0, -0.301, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <circleGeometry args={[0.13, 24]} />
        <meshBasicMaterial color="#ffffff" toneMapped={false} />
      </mesh>

      <spotLight
        ref={spotLightRef}
        position={[0, -0.3, 0]}
        angle={0.35}
        penumbra={0.4}
        intensity={20}
        color="#ffffff"
        castShadow
        shadow-bias={-0.00005}
        shadow-mapSize-width={2048}
        shadow-mapSize-height={2048}
      />

      <mesh position={[0, -2.6, 0]}>
        <cylinderGeometry args={[0.13, 1.0, 5.0, 32, 1, true]} />
        <meshBasicMaterial
          color="#ffffff"
          transparent
          opacity={0.012}
          blending={THREE.AdditiveBlending}
          side={THREE.DoubleSide}
          depthWrite={false}
        />
      </mesh>
    </group>
  );
};

const CeilingGridMaterial = () => {
  const texture = useMemo(() => {
    const canvas = document.createElement("canvas");
    canvas.width = 512;
    canvas.height = 512;
    const ctx = canvas.getContext("2d");

    ctx.fillStyle = "#060606";
    ctx.fillRect(0, 0, 512, 512);

    ctx.strokeStyle = "#121212";
    ctx.lineWidth = 4;
    ctx.strokeRect(0, 0, 512, 512);

    ctx.beginPath();
    ctx.moveTo(256, 0);
    ctx.lineTo(256, 512);
    ctx.moveTo(0, 256);
    ctx.lineTo(512, 256);
    ctx.stroke();

    const tex = new THREE.CanvasTexture(canvas);
    tex.wrapS = THREE.RepeatWrapping;
    tex.wrapT = THREE.RepeatWrapping;
    tex.repeat.set(12, 8);
    return tex;
  }, []);

  return <meshStandardMaterial map={texture} metalness={0.85} roughness={0.15} />;
};

const PremiumShowroomBackground = () => {
  const wallMaterial = useMemo(() => {
    const canvas = document.createElement("canvas");
    canvas.width = 256;
    canvas.height = 256;
    const ctx = canvas.getContext("2d");
    
    ctx.fillStyle = "#0f0f0f";
    ctx.fillRect(0, 0, 256, 256);
    
    for (let i = 0; i < 4000; i++) {
      const x = Math.random() * 256;
      const y = Math.random() * 256;
      const opacity = Math.random() * 0.04;
      ctx.fillStyle = `rgba(255, 255, 255, ${opacity})`;
      ctx.fillRect(x, y, 1, 1);
    }

    const texture = new THREE.CanvasTexture(canvas);
    return new THREE.MeshStandardMaterial({
      map: texture,
      color: "#080808",
      metalness: 0.7,
      roughness: 0.4
    });
  }, []);

  const ShelfBikeSilhouette = () => (
    <group rotation={[0, Math.PI / 2, 0]}>
      <mesh position={[-0.4, 0.22, 0]} castShadow>
        <torusGeometry args={[0.22, 0.022, 6, 16]} />
        <meshStandardMaterial color="#030303" roughness={0.9} />
      </mesh>
      <mesh position={[0.4, 0.22, 0]} castShadow>
        <torusGeometry args={[0.22, 0.022, 6, 16]} />
        <meshStandardMaterial color="#030303" roughness={0.9} />
      </mesh>
      <mesh position={[0, 0.35, 0]} rotation={[0, 0, Math.PI / 4]} castShadow>
        <boxGeometry args={[0.4, 0.02, 0.02]} />
        <meshStandardMaterial color="#0f0f0f" metalness={0.6} />
      </mesh>
      <mesh position={[-0.1, 0.32, 0]} rotation={[0, 0, -Math.PI / 3]} castShadow>
        <boxGeometry args={[0.38, 0.02, 0.02]} />
        <meshStandardMaterial color="#0f0f0f" metalness={0.6} />
      </mesh>
    </group>
  );

  return (
    <group position={[0, 0, -4.5]}>
      {/* Central Brand Signboard Backing Plate */}
      <mesh position={[0, 2.5, -0.05]} receiveShadow>
        <boxGeometry args={[6.2, 2.8, 0.1]} />
        <meshStandardMaterial color="#0c0c0c" metalness={0.8} roughness={0.35} />
      </mesh>

      <mesh position={[0, 2.4, -0.1]} receiveShadow material={wallMaterial}>
        <boxGeometry args={[8.5, 4.8, 0.1]} />
      </mesh>

      <mesh position={[-6.8, 2.4, -0.6]} receiveShadow material={wallMaterial}>
        <boxGeometry args={[5.0, 4.8, 0.1]} />
      </mesh>

      <mesh position={[6.8, 2.4, -0.6]} receiveShadow material={wallMaterial}>
        <boxGeometry args={[5.0, 4.8, 0.1]} />
      </mesh>

      <mesh position={[-4.35, 2.4, 0.15]} castShadow receiveShadow>
        <boxGeometry args={[0.3, 4.8, 0.4]} />
        <meshStandardMaterial color="#060606" metalness={0.85} roughness={0.25} />
      </mesh>

      <mesh position={[4.35, 2.4, 0.15]} castShadow receiveShadow>
        <boxGeometry args={[0.3, 4.8, 0.4]} />
        <meshStandardMaterial color="#060606" metalness={0.85} roughness={0.25} />
      </mesh>

      <mesh position={[-4.18, 2.4, 0.36]}>
        <boxGeometry args={[0.02, 4.8, 0.02]} />
        <meshBasicMaterial color="#ff0b0b" toneMapped={false} />
      </mesh>

      <mesh position={[4.18, 2.4, 0.36]}>
        <boxGeometry args={[0.02, 4.8, 0.02]} />
        <meshBasicMaterial color="#ff0b0b" toneMapped={false} />
      </mesh>

      {/* LEFT BAY SHELVED STORAGE */}
      <group position={[-6.8, 0, -0.5]}>
        <mesh position={[0, 1.2, 0.15]} castShadow>
          <boxGeometry args={[4.2, 0.03, 0.4]} />
          <meshStandardMaterial color="#121212" metalness={0.8} roughness={0.2} />
        </mesh>
        <group position={[-1.1, 1.2, 0.15]}><ShelfBikeSilhouette /></group>
        <group position={[1.1, 1.2, 0.15]}><ShelfBikeSilhouette /></group>

        <mesh position={[0, 2.8, 0.15]} castShadow>
          <boxGeometry args={[4.2, 0.03, 0.4]} />
          <meshStandardMaterial color="#121212" metalness={0.8} roughness={0.2} />
        </mesh>
        <group position={[-1.1, 2.8, 0.15]}><ShelfBikeSilhouette /></group>
        <group position={[1.1, 2.8, 0.15]}><ShelfBikeSilhouette /></group>
      </group>

      {/* RIGHT BAY SHELVED STORAGE */}
      <group position={[6.8, 0, -0.5]}>
        <mesh position={[0, 1.2, 0.15]} castShadow>
          <boxGeometry args={[4.2, 0.03, 0.4]} />
          <meshStandardMaterial color="#121212" metalness={0.8} roughness={0.2} />
        </mesh>
        <group position={[-1.1, 1.2, 0.15]}><ShelfBikeSilhouette /></group>
        <group position={[1.1, 1.2, 0.15]}><ShelfBikeSilhouette /></group>

        <mesh position={[0, 2.8, 0.15]} castShadow>
          <boxGeometry args={[4.2, 0.03, 0.4]} />
          <meshStandardMaterial color="#121212" metalness={0.8} roughness={0.2} />
        </mesh>
        <group position={[-1.1, 2.8, 0.15]}><ShelfBikeSilhouette /></group>
        <group position={[1.1, 2.8, 0.15]}><ShelfBikeSilhouette /></group>
      </group>
    </group>
  );
};

const PlayGame = () => {
  return (
    <div className="playgame-container">
      {/* Cinematic Overlays */}
      <div className="light-bloom" />
      <div className="floor-atmosphere" />
      <div className="vignette" />
      <div className="noise-overlay" />
      <div className="top-red-line" />
      
      {/* Left/Right Vertical Framing Lights */}
      <div className="red-side-glow left" />
      <div className="red-side-glow right" />

      <Canvas
        shadows
        gl={{ antialias: true, alpha: false, preserveDrawingBuffer: true }}
      camera={{
  position: [0, 2.8, 10.5],
  fov: 38
}}
      >
        <color attach="background" args={["#010101"]} />
        <fog attach="fog" args={["#010101", 5, 15]} />
        <Environment preset="night" />
        <ambientLight intensity={0.015} color="#ffffff" />

        {/* Dynamic Overhead Spotlight arrays focused towards center */}

        <CinematicSpotlight position={[-3.5, 5.2, 1.0]} targetX={-1.5} />
        <CinematicSpotlight position={[-1.1, 5.2, 1.0]} targetX={-0.4} />
        <CinematicSpotlight position={[1.1, 5.2, 1.0]} targetX={0.4} />
        <CinematicSpotlight position={[3.5, 5.2, 1.0]} targetX={1.5} />

      {/* =========================================================
    PREMIUM CEILING SYSTEM
========================================================= */}

{/* MAIN CEILING */}

<mesh position={[0, 5.8, 0]}>
  <boxGeometry args={[35, 0.4, 22]} />

  <meshStandardMaterial
    color="#0a0a0a"
    metalness={1}
    roughness={0.12}
  />
</mesh>

{/* CENTER SPINE */}

<mesh position={[0, 4.15, 0]}>
  <boxGeometry args={[0.4, 0.4, 22]} />

  <meshStandardMaterial
    color="#202020"
    metalness={1}
    roughness={0.15}
  />
</mesh>
<mesh position={[0, 4.48, 0]}>
  <boxGeometry args={[0.08, 0.03, 22]} />
  <meshBasicMaterial
    color="#ff0000"
    toneMapped={false}
  />
</mesh>

{/* FRONT BEAM */}

<mesh position={[0, 4.15, 9]}>
  <boxGeometry args={[35, 0.35, 0.4]} />

  <meshStandardMaterial
    color="#2a2a2a"
    metalness={1}
    roughness={0.15}
  />
</mesh>

{/* REAR BEAM */}

<mesh position={[0, 4.15, -9]}>
  <boxGeometry args={[35, 0.35, 0.4]} />

  <meshStandardMaterial
    color="#2a2a2a"
    metalness={1}
    roughness={0.15}
  />
</mesh>

{/* CENTER PANEL */}

<mesh position={[0, 5.01, 0]}>
  <boxGeometry args={[8, 0.05, 22]} />

  <meshStandardMaterial
    color="#303030"
    metalness={1}
    roughness={0.08}
  />
</mesh>

{/* LEFT PANEL */}

<mesh position={[-6, 5.01, 0]}>
  <boxGeometry args={[4, 0.05, 22]} />

  <meshStandardMaterial
    color="#252525"
    metalness={1}
    roughness={0.1}
  />
</mesh>

{/* RIGHT PANEL */}

<mesh position={[6, 5.01, 0]}>
  <boxGeometry args={[4, 0.05, 22]} />

  <meshStandardMaterial
    color="#252525"
    metalness={1}
    roughness={0.1}
  />
</mesh>

{/* RECESSED PANEL */}

<mesh position={[0, 4.82, 0]}>
  <boxGeometry args={[7.2, 0.02, 21]} />

  <meshStandardMaterial
    color="#3a3a3a"
    metalness={1}
    roughness={0.05}
  />
</mesh>

{/* CEILING TILE 1 */}
<mesh position={[-6, 5.0, 0]}>
  <boxGeometry args={[3.4, 0.015, 20]} />
  <meshStandardMaterial
    color="#383838"
    metalness={1}
    roughness={0.08}
  />
</mesh>

{/* CEILING TILE 2 */}
<mesh position={[6, 5.0, 0]}>
  <boxGeometry args={[3.4, 0.015, 20]} />
  <meshStandardMaterial
    color="#383838"
    metalness={1}
    roughness={0.08}
  />
</mesh>
{/* CENTER CEILING TILE */}

<mesh position={[0, 5.0, 0]}>
  <boxGeometry args={[6.8, 0.015, 20]} />

  <meshStandardMaterial
    color="#343434"
    metalness={1}
    roughness={0.08}
  />
</mesh>
<mesh position={[0, 4.84, 0]}>
  <boxGeometry args={[6.9, 0.01, 20.1]} />
  <meshBasicMaterial
    color="#ff0000"
    toneMapped={false}
  />
</mesh>

{/* LEFT INNER TILE */}

<mesh position={[-3, 5.0, 0]}>
  <boxGeometry args={[2.5, 0.015, 20]} />

  <meshStandardMaterial
    color="#383838"
    metalness={1}
    roughness={0.08}
  />
</mesh>
<mesh position={[-6, 4.84, 0]}>
  <boxGeometry args={[3.5, 0.01, 20.1]} />
  <meshBasicMaterial
    color="#ff0000"
    toneMapped={false}
  />
</mesh>

{/* RIGHT INNER TILE */}

<mesh position={[3, 5.0, 0]}>
  <boxGeometry args={[2.5, 0.015, 20]} />

  <meshStandardMaterial
    color="#383838"
    metalness={1}
    roughness={0.08}
  />
</mesh>
<mesh position={[6, 4.84, 0]}>
  <boxGeometry args={[3.5, 0.01, 20.1]} />
  <meshBasicMaterial
    color="#ff0000"
    toneMapped={false}
  />
</mesh>

{/* LEFT LED */}

<mesh position={[-3.2, 5.08, 0]}>
  <boxGeometry args={[0.08, 0.03, 22]} />

  <meshBasicMaterial
    color="#ff0000"
    toneMapped={false}
  />
</mesh>

{/* RIGHT LED */}

<mesh position={[3.2, 5.08, 0]}>
  <boxGeometry args={[0.08, 0.03, 22]} />

  <meshBasicMaterial
    color="#ff0000"
    toneMapped={false}
  />
</mesh>

{/* FRONT LED */}

<mesh position={[0, 5.08, 0]}>
  <boxGeometry args={[35, 0.03, 0.08]} />

  <meshBasicMaterial
    color="#ff0000"
    toneMapped={false}
  />
</mesh>

{/* SPOTLIGHT RAIL */}

<mesh position={[0, 5.35, 1]}>
  <boxGeometry args={[10, 0.12, 0.12]} />

  <meshStandardMaterial
    color="#2c2c2c"
    metalness={1}
    roughness={0.15}
  />
</mesh>

{/* SPOTLIGHT FIXTURES */}

{[-5, -1.8, 1.8, 5].map((x, i) => (
  <mesh key={i} position={[x, 5.15, 1]}>

    <cylinderGeometry
      args={[0.18, 0.22, 0.4, 24]}
    />

    <meshStandardMaterial
      color="#1f1f1f"
      metalness={1}
      roughness={0.12}
    />

  </mesh>
))}
        {/* Structural Running Beams */}
        <mesh position={[-5.0, 4.35, 0]} castShadow>
          <boxGeometry args={[0.8, 0.35, 22]} />
          <meshStandardMaterial color="#080808" metalness={0.85} roughness={0.2} />
        </mesh>
        <mesh position={[-4.58, 4.15, 0]}>
          <boxGeometry args={[0.02, 0.02, 22]} />
          <meshBasicMaterial color="#ff0000" />
        </mesh>

        <mesh position={[5.0, 4.35, 0]} castShadow>
          <boxGeometry args={[0.8, 0.35, 22]} />
          <meshStandardMaterial color="#080808" metalness={0.85} roughness={0.2} />
        </mesh>
        <mesh position={[4.58, 4.15, 0]}>
          <boxGeometry args={[0.02, 0.02, 22]} />
          <meshBasicMaterial color="#ff0000" />
        </mesh>

        {/* 3D Structural Background Layers */}
        <PremiumShowroomBackground />
        <pointLight
  position={[0, 2.7, -4.5]}
  intensity={60}
  distance={14}
  color="#ff0000"
/>
        <pointLight
  position={[0, 2.5, -4]}
  intensity={40}
  distance={10}
  color="#ff0000"
/>
        {/* Deep Ambient Fill Lights for Bays */}
        <pointLight position={[0, 2.2, -4.2]} intensity={25} distance={12} color="#ff0a0a" />
        <pointLight position={[-6.8, 2.0, -4.0]} intensity={8} distance={6} color="#ff1010" />
        <pointLight position={[6.8, 2.0, -4.0]} intensity={8} distance={6} color="#ff1010" />

        {/* Static Linear Backwall Neon Bars */}
        <mesh position={[-5.0, 2.4, -4.35]}>
          <boxGeometry args={[0.03, 4.8, 0.03]} />
          <meshBasicMaterial color="#ff0505" />
        </mesh>
        <mesh position={[5.0, 2.4, -4.35]}>
          <boxGeometry args={[0.03, 4.8, 0.03]} />
          <meshBasicMaterial color="#ff0505" />
        </mesh>

        {/* Polished Tiled Concrete Floor */}
        <mesh rotation={[-Math.PI / 2, 0, 0]} receiveShadow position={[0, 0, 0]}>
          <planeGeometry args={[50, 50]} />
          <meshStandardMaterial color="#020202" metalness={0.98} roughness={0.04} />
        </mesh>
        <mesh
  rotation={[-Math.PI / 2, 0, 0]}
  position={[0, 0.01, -3]}
>
  <planeGeometry args={[12, 0.05]} />

  <meshBasicMaterial
    color="#ff0000"
    toneMapped={false}
  />
</mesh>

        {/* Central Pedestal Pod */}
        <group position={[0, 0, 0.5]}>
         <mesh
  castShadow
  receiveShadow
  position={[0, 0.22, 0]}
>
  <cylinderGeometry args={[4.2, 4.4, 0.55, 80]} />

  <meshStandardMaterial
    color="#040404"
    metalness={0.95}
    roughness={0.18}
  />
</mesh>

          <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.062, 0]}>
            <torusGeometry args={[3.4, 0.015, 16, 100]} />
            <meshBasicMaterial color="#ff0000" />
          </mesh>
          <pointLight position={[0, 0.1, 0]} intensity={8} distance={4} color="#ff0000" />
        </group>
        <group position={[-5.2, 0.05, 2]}>
  <mesh rotation={[0, 0, Math.PI / 7]}>
    <boxGeometry args={[0.08, 0.08, 3]} />
    <meshBasicMaterial color="#ff0000" toneMapped={false} />
  </mesh>

  <pointLight
    position={[0, 0.2, 0]}
    intensity={20}
    distance={6}
    color="#ff0000"
  />
</group>
<group position={[5.2, 0.05, 2]}>
  <mesh rotation={[0, 0, -Math.PI / 7]}>
    <boxGeometry args={[0.08, 0.08, 3]} />
    <meshBasicMaterial color="#ff0000" toneMapped={false} />
  </mesh>

  <pointLight
    position={[0, 0.2, 0]}
    intensity={20}
    distance={6}
    color="#ff0000"
  />
</group>
<mesh position={[-5.8, 0.02, 3]} rotation={[0, 0.18, 0]}>
  <boxGeometry args={[3, 0.02, 0.08]} />
  <meshBasicMaterial color="#ff0000" toneMapped={false} />
</mesh>

<mesh position={[5.8, 0.02, 3]} rotation={[0, -0.18, 0]}>
  <boxGeometry args={[3, 0.02, 0.08]} />
  <meshBasicMaterial color="#ff0000" toneMapped={false} />
</mesh>

        <OrbitControls
          target={[0, 1.3, 0.5]}
          enablePan={false}
          enableZoom={true}
          enableRotate={true}
          minDistance={5}
          maxDistance={11}
          maxPolarAngle={Math.PI / 2 - 0.03}
        />
      </Canvas>

      {/* =========================================================
         NEW & EXTENDED HEADS UP DISPLAY CORES (OVERLAYS)
      ========================================================= */}
      <div className="showroom-ui-overlay">
        
        {/* TOP COMPONENT: APPLICATION BAR */}
        <header className="global-header-bar interactive-ui-element">
          <div className="header-logo-section">
            <span className="logo-indicator"></span>
            <h3>SAC HUB</h3>
          </div>
          <nav className="header-navigation-tabs">
            <button className="nav-tab active">SHOWROOM</button>
            <button className="nav-tab">GARAGE</button>
            <button className="nav-tab">GAME MODES</button>
            <button className="nav-tab">LEADERBOARD</button>
          </nav>
          <div className="header-profile-wallet">
            <div className="wallet-chip">
              <span className="icon">💎</span>
              <span className="value">1,250</span>
            </div>
            <div className="wallet-chip">
              <span className="icon">🪙</span>
              <span className="value">45,000</span>
            </div>
            <div className="user-avatar-badge">LVL 24</div>
          </div>
        </header>

        {/* LEFT COMPONENT: MODEL SELECTOR SIDEBAR */}
        <aside className="left-spec-sidebar interactive-ui-element">
          <div className="sidebar-title-segment">
            <span>COLLECTION</span>
            <h4>FLEET SERIES</h4>
          </div>
          <div className="bike-model-selection-list">
            <button className="model-list-item active">
              <div className="item-selection-indicator" />
              <div>
                <h5>HULK PRO</h5>
                <p>ENDURO / MOUNTAIN</p>
              </div>
            </button>
            <button className="model-list-item">
              <div className="item-selection-indicator" />
              <div>
                <h5>WOLVERINE X3</h5>
                <p>TRAIL / DOWNHILL</p>
              </div>
            </button>
            <button className="model-list-item">
              <div className="item-selection-indicator" />
              <div>
                <h5>X-MEN PRIME</h5>
                <p>CROSS COUNTRY</p>
              </div>
            </button>
            <button className="model-list-item">
              <div className="item-selection-indicator" />
              <div>
                <h5>SPEEDSTER</h5>
                <p>TRACK RACING</p>
              </div>
            </button>
          </div>
        </aside>

        {/* RIGHT COMPONENT: DETAILED PERFORMANCE STATISTICS */}
        <aside className="right-stats-sidebar interactive-ui-element">
          <div className="sidebar-title-segment">
            <span>SPECIFICATIONS</span>
            <h4>PERFORMANCE DATA</h4>
          </div>
          <div className="performance-metrics-stack">
            <div className="metric-row">
              <div className="metric-labels">
                <span>FRAME RIGIDITY</span>
                <span className="metric-value">92%</span>
              </div>
              <div className="metric-progress-track">
                <div className="metric-progress-fill" style={{ width: "92%" }} />
              </div>
            </div>

            <div className="metric-row">
              <div className="metric-labels">
                <span>SUSPENSION TRAVEL</span>
                <span className="metric-value">85%</span>
              </div>
              <div className="metric-progress-track">
                <div className="metric-progress-fill" style={{ width: "85%" }} />
              </div>
            </div>

            <div className="metric-row">
              <div className="metric-labels">
                <span>BRAKING RESPONSE</span>
                <span className="metric-value">78%</span>
              </div>
              <div className="metric-progress-track">
                <div className="metric-progress-fill" style={{ width: "78%" }} />
              </div>
            </div>

            <div className="metric-row">
              <div className="metric-labels">
                <span>TRACTION COEFFICIENT</span>
                <span className="metric-value">89%</span>
              </div>
              <div className="metric-progress-track">
                <div className="metric-progress-fill" style={{ width: "89%" }} />
              </div>
            </div>
          </div>

          <div className="spec-bulletins-container">
            <div className="bulletin-node">
              <h6>CARBON FIBER MATRIX</h6>
              <p>Reinforced custom composite construction frame layout.</p>
            </div>
            <div className="bulletin-node">
              <h6>HYDRAULIC ACTUATION</h6>
              <p>Quad-piston high thermal management deceleration system.</p>
            </div>
          </div>
        </aside>

       {/* CENTER COMPONENT: BRAND CAPTION */}
<div className="center-stage-copy">
  <div className="brand-tag">PREMIUM SHOWROOM</div>
  <h1 className="brand-title">
    SAC
    <span className="brand-icon-wrapper">
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M3 21H21L12 4L3 21ZM12 9.8L17.5 19H6.5L12 9.8Z" fill="currentColor"/>
      </svg>
    </span>
    <span>MTB</span>
  </h1>
  <p className="brand-subtitle">RIDE YOUR DREAMS</p>

  {/* RELOCATED ASSISTANT CARD WINDOW HERE */}
  <div className="assistant-card interactive-ui-element">
    <div className="assistant-label">SAC ASSISTANT</div>
    <p>
      Hello Rider! This is our HULK PRO Fat Tyre MTB. Built for all terrains and extreme adventures. Would you like to know more about it?
    </p>
    <div className="assistant-actions">
      <button className="primary-btn">YES, TELL ME MORE</button>
      <button className="ghost-btn">NO, THANKS</button>
    </div>
  </div>
</div>


        {/* PROGRESS NAVIGATION STRIP */}
        <div className="mode-strip interactive-ui-element">
          <div className="mode-card active">
            <span className="mode-step">01</span>
            <div>
              <h4>Showroom</h4>
              <p>Enter the premium scene</p>
            </div>
          </div>

          <div className="mode-card">
            <span className="mode-step">02</span>
            <div>
              <h4>Customize</h4>
              <p>Style comes next</p>
            </div>
          </div>

          <div className="mode-card">
            <span className="mode-step">03</span>
            <div>
              <h4>Test Ride</h4>
              <p>Feel the experience</p>
            </div>
          </div>

          <div className="mode-card">
            <span className="mode-step">04</span>
            <div>
              <h4>Play</h4>
              <p>Interactive mode</p>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default PlayGame;